import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import toastr from "toastr";
import { CustomLoading } from "components/CustomLoading";
import {
  Wallet,
  ShieldCheck,
  AlertTriangle,
  Repeat,
  Landmark,
  Globe,
  FileText,
  FileDown,
  ArrowRight,
  ArrowLeft,
  Lock,
  Sparkles,
  Receipt,
  Banknote,
  Building2,
  Send,
  Check,
  CheckCircle2,
  Copy,
  Pencil,
  ChevronDown,
  CalendarClock,
  Info,
  Mail,
  Users,
  XCircle,
  BellRing,
} from "lucide-react";
import base64 from "../../data/base64.json";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import "../../styles/Funding.css";
import { downloadPadAgreement } from "./PadAgreementPdf";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

const FUNDING_EMAIL_FREQ = [
  { value: 1, label: "1x per week (Monday)" },
  { value: 3, label: "3x per week (Monday, Wednesday, Friday)" },
  { value: 5, label: "5x per week (Monday to Friday)" },
];

// verbatim legacy PAD agreement (FlexReportApp PadDashboard/Index.cshtml §6)
const PAD_AGREEMENT = [
  "I/we authorize National HealthClaim to begin deductions for weekly regular recurring payments and/or one-time payments from time to time, for payment of all spending account funding charges arising under my/our National HealthClaim account(s). Weekly payments, as needed, to maintain the minimum account balance for this spending account will be debited from my/our specified account on the Thursday of each week. Upon approval, National HealthClaim will establish the account balance based on the company wide assigned employee limits for the current benefit year. You will receive a withdrawal email notification earlier that day (Thursday morning) for the amount of the debit. These funds will be credited to your National HealthClaim account 5 days after the debit, pending no payment failure notice has been received from your financial institution. If a payment failure has been sent to National HealthClaim, we will turn off your Pre-Authorized Debit payments until the issue has been resolved and you have contacted National HealthClaim to resume your Pre-Authorized funding payments.",
  "This authority is to remain in effect until National HealthClaim has received written notification from me/us of its change or termination. This notification must be received at least five (5) business days before the next debit is scheduled. For any questions / inquiries / edits, Please contact National HealthClaim at support@nationalhealthclaim.com.",
  "National HealthClaim may not assign this authorization, whether directly or indirectly, by operation of law, change of control or otherwise, without providing at least 5 days prior written notice to me.",
];

const PAD_HOW = [
  "Debits happen every Thursday, only when your account needs it. If your balance already covers your float and all pending claims, no debit occurs that week.",
  "Each debit covers only what's needed: a top-up back to your weekly float, plus any approved claims awaiting reimbursement.",
  "You get a Thursday-morning email with the exact amount before it's debited.",
  "Funds are credited to your National HealthClaim account 5 business days after the debit.",
];

// legacy funding note (Record.tsx funding_option), reworded
const FUNDING_NOTE =
  "National HealthClaim will only issue reimbursements once a company cheque or online payment has cleared into our account. In the event of over-funding, the balance will remain as a credit on your account indefinitely for payment of future claims. You may also contact us for a refund of any uncommitted funding balance on our end. There is no interest paid on a positive balance.";

type Method = "pad" | "obp" | "wire" | "etransfer" | "cheque";

type PadState =
  | "setup"
  | "pending_first_debit"
  | "active"
  | "suspended_failed"
  | "suspended_request"
  | "inactive";

interface PadStatus {
  state: PadState;
  enrolled: boolean;
  enablePad: boolean;
  bankNo: string | null;
  transitNo: string | null;
  bankAccountMasked: string | null;
  floatAmount: number;
  authorizedByName: string | null;
  authorizedAt: string | null;
  authorizedByEmail: string | null;
  policyNo: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  hasDebits: boolean;
  annualLimits: number;
  suggestedFloat: number;
}

interface FundingSummary {
  fundingBalance: number;
  onHoldAmount: number;
  approvedClaimCount: number;
  approvedClaimTotal: number;
  shortfall: number;
}

interface PolicyHolderInfo {
  name: string;
  street: string;
  city: string;
  prov: string;
  postal: string;
}

interface Props {
  policyId: string;
  isBusinessOwner?: boolean;
  fundingEmailFrequency?: number;
  policyHolder?: PolicyHolderInfo;
}

const OTHER_METHODS: { key: Method; title: string; Icon: any; timeline: string }[] = [
  { key: "obp", title: "Online Bill Payment", Icon: Globe, timeline: "Close to 2-3 business days" },
  { key: "wire", title: "Direct Deposit & Wire", Icon: Building2, timeline: "EFT / wire details on request" },
  { key: "etransfer", title: "Interac e-Transfer", Icon: Send, timeline: "Send to support@nationalhealthclaim.com" },
  { key: "cheque", title: "Company Cheque", Icon: FileText, timeline: "10 calendar days after arrival at office" },
];

const fmt = (n: number) =>
  "$" + Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// same rule as the server: all but the last 3 digits
const maskDraft = (a: string) => (a && a.length > 3 ? "•".repeat(a.length - 3) + a.slice(-3) : "•".repeat(a?.length || 0));

const MONEY_RE = /^\d*\.?\d{0,2}$/;

const fmtDate = (iso: string | null) =>
  iso
    ? new Date(/[zZ]$|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + "Z").toLocaleDateString("en-CA", {
        timeZone: "America/Edmonton",
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

export default function FundingTab(props: Props) {
  const { policyId } = props;

  const [loading, setLoading] = useState(true);
  const [pad, setPad] = useState<PadStatus | null>(null);
  const [summary, setSummary] = useState<FundingSummary | null>(null);
  const [payeeAccountNumber, setPayeeAccountNumber] = useState("");
  const [payeeCopied, setPayeeCopied] = useState(false);
  const [minimumBalance, setMinimumBalance] = useState<number | null>(null);
  const [readOnly, setReadOnly] = useState(false);

  // pad setup wizard
  const [showWizard, setShowWizard] = useState(false);
  const [padStep, setPadStep] = useState(1);
  const [transit, setTransit] = useState("");
  const [institution, setInstitution] = useState("");
  const [account, setAccount] = useState("");
  const [transitConfirm, setTransitConfirm] = useState("");
  const [institutionConfirm, setInstitutionConfirm] = useState("");
  const [accountConfirm, setAccountConfirm] = useState("");
  const [floatAmount, setFloatAmount] = useState("");
  const [signingName, setSigningName] = useState("");
  const [authorized, setAuthorized] = useState(false);
  const [bankTouched, setBankTouched] = useState(false);
  const [justEnrolled, setJustEnrolled] = useState(false);

  // active-state management
  const [method, setMethod] = useState<Method>("pad");
  const [editBanking, setEditBanking] = useState(false);
  const [editFloat, setEditFloat] = useState(false);
  const [bankConsent, setBankConsent] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [confirmFloat, setConfirmFloat] = useState(false);

  // comm settings + shortfall
  const [emailFreq, setEmailFreq] = useState(
    [1, 3, 5].includes(Number(props.fundingEmailFrequency)) ? Number(props.fundingEmailFrequency) : 3
  );
  const [editMinBal, setEditMinBal] = useState(false);
  const [minBalDraft, setMinBalDraft] = useState("");
  const [shortfallAmount, setShortfallAmount] = useState("");

  const [busy, setBusy] = useState(false);

  const fetchPadStatus = useCallback(async () => {
    const res = await axios.get(`/api/pad/status?policyId=${policyId}`);
    if (res.data.success) {
      setPad(res.data);
    }
  }, [policyId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [statusRes, fundingRes, payeeRes, minBalRes, roRes] = await Promise.all([
          axios.get(`/api/pad/status?policyId=${policyId}`),
          axios.get(`/api/PlanAdmin/funding?policyId=${policyId}`),
          axios.get(`/api/PlanAdmin/payeeaccountnumber?policyId=${policyId}`),
          axios.get(`/api/PlanAdmin/minimumbalance?policyId=${policyId}`),
          axios.get(`/api/PlanAdmin/readonly?policyId=${policyId}`),
        ]);
        if (!alive) return;
        if (statusRes.data.success) {
          setPad(statusRes.data);
          setFloatAmount((statusRes.data.suggestedFloat || 0).toFixed(2));
        }
        setSummary(fundingRes.data);
        setPayeeAccountNumber(payeeRes.data.spendingAccountNumber || "");
        setMinimumBalance(minBalRes.data.minimumBalance ?? null);
        setReadOnly(roRes.data === true);
      } catch {
        if (alive) toastr.error("Unable to load funding information.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [policyId, fetchPadStatus]);

  const floatNum = parseFloat(floatAmount) || 0;
  const today = new Date().toLocaleDateString("en-CA", { year: "numeric", month: "long", day: "numeric" });
  const nextThursday = (() => {
    const d = new Date();
    d.setDate(d.getDate() + ((4 - d.getDay() + 7) % 7 || 7));
    return d.toLocaleDateString("en-CA", { month: "long", day: "numeric" });
  })();

  const state: PadState = pad?.state || "setup";
  const padConfigured = !!pad?.enrolled;
  const padRunning = state === "active" || state === "pending_first_debit";

  const bankErr = {
    transit: !/^\d{5}$/.test(transit),
    institution: !/^\d{3}$/.test(institution),
    account: !/^\d{7,12}$/.test(account),
    transitConfirm: transit !== "" && transitConfirm !== transit,
    institutionConfirm: institution !== "" && institutionConfirm !== institution,
    accountConfirm: account !== "" && accountConfirm !== account,
    float: floatAmount.trim() === "" || floatNum < 0,
  };

  const fieldHint = (err: boolean, normal: string, error: string) => (
    <div className={"fundingv2-hint" + (err ? " fundingv2-hint--error" : "")}>{err ? error : normal}</div>
  );
  const inputCls = (err: boolean) =>
    "fundingv2-input fundingv2-input--mono" + (err ? " fundingv2-input--error" : "");

  const resetDrafts = () => {
    setPadStep(1);
    setTransit("");
    setInstitution("");
    setAccount("");
    setTransitConfirm("");
    setInstitutionConfirm("");
    setAccountConfirm("");
    setSigningName("");
    setAuthorized(false);
    setBankConsent(false);
    setFloatAmount((pad?.suggestedFloat || 0).toFixed(2));
    setBankTouched(false);
    setEditBanking(false);
    setEditFloat(false);
    setShowCancel(false);
    setConfirmFloat(false);
  };

  const bankStepInvalid = () =>
    bankErr.transit || bankErr.institution || bankErr.account || bankErr.transitConfirm || bankErr.institutionConfirm || bankErr.accountConfirm || bankErr.float;

  const continueBanking = () => {
    if (bankStepInvalid()) {
      setBankTouched(true);
      return;
    }
    setBankTouched(false);
    setConfirmFloat(true);
  };

  const confirmFloatContinue = () => {
    if (bankStepInvalid()) {
      setBankTouched(true);
      setConfirmFloat(false);
      return;
    }
    setConfirmFloat(false);
    setPadStep(3);
  };

  const submitPad = async () => {
    if (!signingName.trim()) {
      toastr.error("Enter the signing officer's name.");
      return;
    }
    if (!authorized) {
      toastr.error("Check the authorization box to continue.");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post("/api/pad/enroll", {
        policyId,
        bankNo: institution,
        transitNo: transit,
        bankAccountNo: account,
        floatAmount: floatNum,
        signingName: signingName.trim(),
        consent: authorized,
      });
      if (res.data.success) {
        setShowWizard(false);
        setJustEnrolled(true);
        toastr.success("Pre-Authorized Debit authorization submitted.");
        // enrollment is committed; a failed refresh here must not read as a failed enroll
        try {
          await fetchPadStatus();
        } catch {
          /* status refreshes on next visit */
        }
      } else {
        toastr.error(res.data.message || "Unable to submit the authorization.");
      }
    } catch {
      toastr.error("Unable to submit the authorization.");
    } finally {
      setBusy(false);
    }
  };

  const openEditBanking = () => {
    setTransit(pad?.transitNo || "");
    setInstitution(pad?.bankNo || "");
    setAccount(""); // never prefill the account number
    setTransitConfirm("");
    setInstitutionConfirm("");
    setAccountConfirm("");
    setBankConsent(false);
    setBankTouched(false);
    setEditFloat(false);
    setEditBanking(true);
  };

  const saveBanking = async () => {
    // blank account = keep the one on file
    if (bankErr.transit || bankErr.institution || (account !== "" && bankErr.account) || bankErr.accountConfirm) {
      setBankTouched(true);
      return;
    }
    if (!bankConsent) {
      toastr.error("Check the confirmation box to continue.");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post("/api/pad/banking", {
        policyId,
        bankNo: institution,
        transitNo: transit,
        bankAccountNo: account,
        consent: bankConsent,
      });
      if (res.data.success) {
        await fetchPadStatus();
        setEditBanking(false);
        toastr.success("Banking details updated.");
      } else {
        toastr.error(res.data.message || "Unable to update banking details.");
      }
    } catch {
      toastr.error("Unable to update banking details.");
    } finally {
      setBusy(false);
    }
  };

  const openEditFloat = () => {
    setFloatAmount((pad?.floatAmount ?? 0).toFixed(2));
    setEditBanking(false);
    setEditFloat(true);
  };

  const saveFloat = async () => {
    if (bankErr.float) {
      toastr.error("Enter a weekly float amount ($0 or more).");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post("/api/pad/float", { policyId, floatAmount: floatNum });
      if (res.data.success) {
        await fetchPadStatus();
        setEditFloat(false);
        toastr.success("Weekly float updated.");
      } else {
        toastr.error(res.data.message || "Unable to update the float amount.");
      }
    } catch {
      toastr.error("Unable to update the float amount.");
    } finally {
      setBusy(false);
    }
  };

  const submitCancel = async () => {
    setBusy(true);
    try {
      const res = await axios.post("/api/pad/cancelrequest", { policyId });
      if (res.data.success) {
        await fetchPadStatus();
        setShowCancel(false);
        toastr.success("Pre-Authorized Debit has been paused. Contact support to reactivate.");
      } else {
        toastr.error(res.data.message || "Unable to submit the cancellation request.");
      }
    } catch {
      toastr.error("Unable to submit the cancellation request.");
    } finally {
      setBusy(false);
    }
  };

  const changeEmailFreq = async (value: number) => {
    const prev = emailFreq;
    setEmailFreq(value);
    try {
      const res = await axios.put(
        `/api/PlanAdmin/updatefundingemailfrequency?policyId=${policyId}&frequency=${value}`
      );
      if (res.data.success) {
        toastr.success("Funding email frequency updated", "Success");
      } else {
        setEmailFreq(prev);
        toastr.error(res.data.message, "Error");
      }
    } catch {
      setEmailFreq(prev);
      toastr.error("Unable to update the email frequency.");
    }
  };

  const saveMinimumBalance = async () => {
    const val = parseFloat(minBalDraft);
    if (minBalDraft.trim() === "" || isNaN(val) || val < 0) {
      toastr.error("Enter a minimum balance ($0 or more).");
      return;
    }
    setBusy(true);
    try {
      await axios.put("/api/PlanAdmin/updateminimumbalance", {
        PolicyId: policyId,
        MinimumBalance: val,
      });
      await axios.put("/api/PlanAdmin/updatepolicylogs", {
        Note: "Minimum Balance: " + val,
        Action: "Policy Update Balance Notification",
        PolicyId: policyId,
      });
      const res = await axios.get(`/api/PlanAdmin/minimumbalance?policyId=${policyId}`);
      setMinimumBalance(res.data.minimumBalance ?? null);
      setEditMinBal(false);
      toastr.success("Notification settings updated", "Success");
    } catch {
      toastr.error("Unable to update the minimum balance.");
    } finally {
      setBusy(false);
    }
  };

  // only self-serve enrollments have an electronic signature on record
  const agreementReady = !!(pad?.authorizedByName && pad?.authorizedAt);

  const downloadAgreement = () => {
    if (!pad?.authorizedByName || !pad.authorizedAt) return;
    const ph = props.policyHolder;
    downloadPadAgreement({
      companyName: ph?.name || "",
      companyAddress: [ph?.street, ph?.city, [ph?.prov, ph?.postal].filter(Boolean).join(" ")]
        .filter(Boolean)
        .join(", "),
      policyNo: pad.policyNo || "",
      bankNo: pad.bankNo || "",
      transitNo: pad.transitNo || "",
      bankAccountMasked: pad.bankAccountMasked || "",
      floatAmount: pad.floatAmount || 0,
      authorizedByName: pad.authorizedByName,
      authorizedByEmail: pad.authorizedByEmail,
      authorizedAt: pad.authorizedAt,
    });
  };

  const agreementLink = () =>
    agreementReady ? (
      <div className="fundingv2-actionrow">
        <button type="button" className="fundingv2-actionlink" onClick={downloadAgreement}>
          <FileDown size={13} /> Download PAD Agreement (PDF)
        </button>
      </div>
    ) : null;

  // same document as the legacy Record.tsx shortfall form
  const generateShortfallForm = () => {
    const amount = parseFloat(shortfallAmount);
    if (!amount || isNaN(amount)) {
      toastr.error("Enter an amount first.");
      return;
    }
    const ph = props.policyHolder;
    const now = new Date();
    const timestamp = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, "0")}-${now
      .getDate()
      .toString()
      .padStart(2, "0")} ${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;
    const currDate = now.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

    const content = {
      content: [
        {
          columns: [
            { text: "", width: "*" },
            { image: base64.logo, width: 200, margin: [0, 0, 0, 0] },
          ],
          columnGap: 10,
        },
        {
          columns: [
            { text: "", width: "*" },
            {
              stack: ["National HealthClaim Corp (FC)", "5320 - 7005 Fairmount Dr. SE", "Calgary, AB", "T2H 0K4"],
              alignment: "right",
              margin: [20, 20, 0, 30],
            },
          ],
        },
        {
          columns: [
            {
              stack: [ph?.name || "", ph?.street || "", (ph?.city || "") + ", " + (ph?.prov || ""), ph?.postal || ""],
              alignment: "left",
              margin: [0, 0, 0, 10],
            },
            { stack: [currDate], alignment: "right", margin: [0, 0, 0, 10] },
          ],
        },
        {
          columns: [
            { stack: ["Shortfall funding request " + timestamp], alignment: "left", margin: [0, 10, 0, 30], bold: true },
          ],
        },
        {
          columns: [
            {
              stack: [
                'This is an interim shortfall funding request form. A formal monthly statement will be automatically generated at the conclusion of each month, providing a comprehensive breakdown of funding distribution. You can locate this statement in the Reports Tab under the label "Monthly Statement".',
              ],
              alignment: "left",
              margin: [0, 0, 0, 15],
            },
          ],
        },
        {
          columns: [
            {
              stack: [
                {
                  text:
                    "Amount of " +
                    Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(
                      amount
                    ),
                  bold: true,
                },
                "\n",
                "Company Name: ",
                { text: ph?.name || "", bold: true },
                "\n",
                "Account Number: ",
                { text: payeeAccountNumber, bold: true },
              ],
              alignment: "left",
              margin: [0, 10, 0, 0],
            },
          ],
        },
        {
          columns: [
            {
              stack: [
                "Note: The amount in the form may not accurately represent the current shortfall of the account, as shortfalls are subject to change. The indicated amount is the intended funding to be paid at the time the form was generated. Kindly retain this form for record-keeping purposes.",
              ],
              fontSize: 10,
              alignment: "left",
              margin: [0, 20, 0, 0],
            },
          ],
        },
      ],
    };

    pdfMake.createPdf(content as any).download("FundingRequest" + payeeAccountNumber);
  };

  const copyPayeeNumber = async () => {
    try {
      await navigator.clipboard.writeText(payeeAccountNumber);
      setPayeeCopied(true);
      setTimeout(() => setPayeeCopied(false), 2000);
    } catch {
      toastr.error("Could not copy. Please copy the number manually.");
    }
  };

  const renderMethodBody = (key: Method) => {
    switch (key) {
      case "obp":
        return (
          <>
            <div className="fundingv2-callout">
              <div className="fundingv2-callout__label">Payee Account Number</div>
              <div className="fundingv2-callout__row">
                <div className="fundingv2-callout__val">{payeeAccountNumber || "-"}</div>
                {payeeAccountNumber && (
                  <button type="button" className="fundingv2-editlink" aria-label="Copy payee account number" onClick={copyPayeeNumber}>
                    {payeeCopied ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                  </button>
                )}
              </div>
            </div>
            <p className="fundingv2-muted">
              Log in to your company's online banking and add National HealthClaim as a "Payee" for the Spending Account. Enter the Payee Account Number above, then fund the account using your bank's "Pay Bills" feature. Clearing is close to 2-3 business days and there is nothing to mail.
            </p>
          </>
        );
      case "wire":
        return (
          <p className="fundingv2-muted">
            Contact <strong>support@nationalhealthclaim.com</strong> and we'll send you our EFT (direct deposit) and wire transfer details for this funding option.
          </p>
        );
      case "etransfer":
        return (
          <p className="fundingv2-muted">
            Send your Interac e-Transfer to <strong>support@nationalhealthclaim.com</strong>. You must include your <strong>policy number</strong>, <strong>payee account number</strong>, or <strong>company name</strong> in the transfer message.
          </p>
        );
      case "cheque":
        return (
          <p className="fundingv2-muted">
            Make your cheque payable to National HealthClaim and mail it to our office. Contact <strong>support@nationalhealthclaim.com</strong> if you need the mailing address. Reimbursements are issued once the cheque has cleared into our account, 10 calendar days after it arrives.
          </p>
        );
      default:
        return null;
    }
  };

  const bankingFields = (idPrefix: string, accountOptional: boolean) => (
    <>
      <div className="fundingv2-field">
        <label htmlFor={idPrefix + "-transit"}>Transit No.</label>
        <input
          id={idPrefix + "-transit"}
          className={inputCls(bankTouched && bankErr.transit)}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          maxLength={5}
          value={transit}
          placeholder="00000"
          onChange={(e) => setTransit(e.target.value.replace(/\D/g, ""))}
        />
        {fieldHint(bankTouched && bankErr.transit, "Your 5-digit branch transit number.", "Transit number must be 5 digits.")}
      </div>
      {!accountOptional && (
        <div className="fundingv2-field fundingv2-field--confirm">
          <label htmlFor={idPrefix + "-transit2"}>Re-enter Transit No.</label>
          <input
            id={idPrefix + "-transit2"}
            className={inputCls(bankTouched && bankErr.transitConfirm)}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            maxLength={5}
            value={transitConfirm}
            placeholder="00000"
            onChange={(e) => setTransitConfirm(e.target.value.replace(/\D/g, ""))}
          />
          {fieldHint(bankTouched && bankErr.transitConfirm, "Type your transit number again to confirm.", "Transit numbers must match.")}
        </div>
      )}
      <div className="fundingv2-field">
        <label htmlFor={idPrefix + "-institution"}>Institution (Bank) No.</label>
        <input
          id={idPrefix + "-institution"}
          className={inputCls(bankTouched && bankErr.institution)}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          maxLength={3}
          value={institution}
          placeholder="000"
          onChange={(e) => setInstitution(e.target.value.replace(/\D/g, ""))}
        />
        {fieldHint(bankTouched && bankErr.institution, "Your bank's 3-digit institution code.", "Institution (bank) number must be 3 digits.")}
      </div>
      {!accountOptional && (
        <div className="fundingv2-field fundingv2-field--confirm">
          <label htmlFor={idPrefix + "-institution2"}>Re-enter Institution No.</label>
          <input
            id={idPrefix + "-institution2"}
            className={inputCls(bankTouched && bankErr.institutionConfirm)}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            maxLength={3}
            value={institutionConfirm}
            placeholder="000"
            onChange={(e) => setInstitutionConfirm(e.target.value.replace(/\D/g, ""))}
          />
          {fieldHint(bankTouched && bankErr.institutionConfirm, "Type your institution number again to confirm.", "Institution numbers must match.")}
        </div>
      )}
      <div className="fundingv2-field">
        <label htmlFor={idPrefix + "-account"}>Account No.</label>
        <input
          id={idPrefix + "-account"}
          className={inputCls(bankTouched && (accountOptional ? account !== "" && bankErr.account : bankErr.account))}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          maxLength={12}
          value={account}
          placeholder={accountOptional ? pad?.bankAccountMasked || "" : "0000000"}
          onChange={(e) => setAccount(e.target.value.replace(/\D/g, ""))}
        />
        {accountOptional
          ? fieldHint(bankTouched && account !== "" && bankErr.account, "Leave blank to keep " + (pad?.bankAccountMasked || "the account on file") + ".", "Account number must be 7 to 12 digits.")
          : fieldHint(bankTouched && bankErr.account, "7 to 12 digits. The account we debit.", "Account number must be 7 to 12 digits.")}
      </div>
      {/* confirm only once a new account number is being entered */}
      {(!accountOptional || account !== "") && (
        <div className="fundingv2-field fundingv2-field--confirm">
          <label htmlFor={idPrefix + "-account2"}>Re-enter Account No.</label>
          <input
            id={idPrefix + "-account2"}
            className={inputCls(bankTouched && bankErr.accountConfirm)}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            maxLength={12}
            value={accountConfirm}
            placeholder={accountOptional ? "" : "0000000"}
            onChange={(e) => setAccountConfirm(e.target.value.replace(/\D/g, ""))}
          />
          {fieldHint(bankTouched && bankErr.accountConfirm, "Type your account number again to confirm.", "Account numbers must match.")}
        </div>
      )}
    </>
  );

  const howPanel = (intro = false) => (
    <div className="fundingv2-howpanel">
      <div className="fundingv2-howpanel__title"><Info size={14} /> How Pre-Authorized Debit works</div>
      {intro && (
        <p className="fundingv2-howpanel__lead">
          Pre-Authorized Debit automates your account funding. Your bank account is debited on a set schedule so your National HealthClaim account stays funded without manual transfers.
        </p>
      )}
      <ul>
        {PAD_HOW.map((b, i) => <li key={i}><Check size={14} /><span>{b}</span></li>)}
        {intro && <li><Check size={14} /><span>Your first debit will be the Thursday after your setup is complete.</span></li>}
      </ul>
    </div>
  );

  const howFooter = () => (
    <details className="fundingv2-howfoot">
      <summary>
        <Info size={14} /> How Pre-Authorized Debit works
        <ChevronDown size={15} className="fundingv2-howfoot__chev" />
      </summary>
      {howPanel()}
    </details>
  );

  const suspendedBody = () => {
    let title: string;
    let desc: string;
    if (state === "suspended_failed") {
      title = "Your last PAD debit was unsuccessful, so automatic debits are paused";
      desc = "Contact support@nationalhealthclaim.com for details or to reactivate Pre-Authorized Debit.";
    } else if (state === "suspended_request") {
      title = "PAD was paused at your request. No automatic debits are occurring";
      desc = "To reactivate Pre-Authorized Debit, contact support@nationalhealthclaim.com. Debits resume the Thursday after reactivation.";
    } else {
      title = "Pre-Authorized Debit is currently turned off on your account";
      desc = "To reactivate Pre-Authorized Debit, contact support@nationalhealthclaim.com.";
    }
    return (
      <div className="fundingv2-pad" onClick={(e) => e.stopPropagation()}>
        <div className={"fundingv2-banner " + (state === "suspended_failed" ? "fundingv2-banner--warn" : "fundingv2-banner--neutral")}>
          {state === "suspended_failed" ? <AlertTriangle size={15} /> : <Info size={15} />}
          <div>
            <div className="fundingv2-banner__title">{title}</div>
            <div>
              {desc.split("support@nationalhealthclaim.com")[0]}
              <a href="mailto:support@nationalhealthclaim.com">support@nationalhealthclaim.com</a>
              {desc.split("support@nationalhealthclaim.com")[1]}
            </div>
          </div>
        </div>
        <div className="fundingv2-detailgrid">
          <div className="fundingv2-detail">
            <div className="fundingv2-detail__head">
              <span className="fundingv2-detail__label">Banking on file</span>
              {!editBanking && !readOnly && (
                <button type="button" className="fundingv2-editlink" onClick={openEditBanking}><Pencil size={13} /> Edit</button>
              )}
            </div>
            {!editBanking ? (
              <>
                <div className="fundingv2-detail__row"><span>Transit</span><strong>{pad?.transitNo || "-"}</strong></div>
                <div className="fundingv2-detail__row"><span>Institution</span><strong>{pad?.bankNo || "-"}</strong></div>
                <div className="fundingv2-detail__row"><span>Account</span><strong>{pad?.bankAccountMasked || "-"}</strong></div>
                {state === "suspended_failed" && !readOnly && (
                  <div className="fundingv2-hint">If your banking details need updating, edit them here before contacting support.</div>
                )}
              </>
            ) : (
              <>
                <div className="fundingv2-editfields">{bankingFields("pad-edit", true)}</div>
                <label className="fundingv2-consent" style={{ fontSize: 13 }}>
                  <input type="checkbox" checked={bankConsent} onChange={(e) => setBankConsent(e.target.checked)} />
                  <span>I confirm these bank details are correct and I am authorized to debit this account.</span>
                </label>
                <div className="fundingv2-actions">
                  <button className="fundingv2-btn fundingv2-btn--ghost fundingv2-btn--sm" onClick={() => setEditBanking(false)}>Cancel</button>
                  <button className="fundingv2-btn fundingv2-btn--sm" disabled={busy} onClick={saveBanking}><Check size={15} /> Save</button>
                </div>
              </>
            )}
          </div>
          <div className="fundingv2-detail">
            <div className="fundingv2-detail__head">
              <span className="fundingv2-detail__label">Weekly Float</span>
            </div>
            <div className="fundingv2-detail__val">{fmt(pad?.floatAmount || 0)}</div>
          </div>
        </div>
        {agreementLink()}
      </div>
    );
  };

  const confirmationBody = () => (
    <div className="fundingv2-pad" onClick={(e) => e.stopPropagation()}>
      <div className="fundingv2-confirm__title">
        <CheckCircle2 size={18} color="#2f7a5b" /> You're all set.
      </div>
      <div className="fundingv2-confirm__sub">Pre-Authorized Debit is now active on your account.</div>
      <div className="fundingv2-confirm__items">
        <div className="fundingv2-confirm__item">
          <CalendarClock size={15} />
          <div>
            <div className="fundingv2-confirm__label">Your first debit</div>
            <div className="fundingv2-confirm__desc">
              The Thursday after today. You'll receive a Thursday-morning email with the exact amount before it processes. If your balance already covers your float and nothing is pending, no debit will occur.
            </div>
          </div>
        </div>
        <div className="fundingv2-confirm__item">
          <Repeat size={15} />
          <div>
            <div className="fundingv2-confirm__label">Your weekly rhythm</div>
            <div className="fundingv2-confirm__desc">
              Every Thursday we check your account. If a debit is needed, you'll get a morning email before it processes. If not, you won't hear from us that week.
            </div>
          </div>
        </div>
        <div className="fundingv2-confirm__item">
          <Mail size={15} />
          <div>
            <div className="fundingv2-confirm__label">Funds clearing</div>
            <div className="fundingv2-confirm__desc">
              Funds are credited to your National HealthClaim account 5 business days after each debit.
            </div>
          </div>
        </div>
        <div className="fundingv2-confirm__item">
          <Users size={15} />
          <div>
            <div className="fundingv2-confirm__label">Who receives PAD emails</div>
            <div className="fundingv2-confirm__desc">
              All plan administrators on your policy who are set up to receive emails will get the Thursday debit notifications.
            </div>
          </div>
        </div>
      </div>
      <div className="fundingv2-confirm__foot">
        To update your banking details or float amount, return to this tab at any time. To cancel or pause PAD, contact{" "}
        <a href="mailto:support@nationalhealthclaim.com">support@nationalhealthclaim.com</a>. Written notice is required at least 5 business days before the next scheduled debit.
      </div>
      {agreementLink()}
      <div className="fundingv2-actions">
        <button className="fundingv2-btn" onClick={() => setJustEnrolled(false)}>Back to Funding</button>
      </div>
      {howFooter()}
    </div>
  );

  const activeBody = () => (
    <div className="fundingv2-pad" onClick={(e) => e.stopPropagation()}>
      {state === "pending_first_debit" && !justEnrolled && (
        <div className="fundingv2-banner fundingv2-banner--info">
          <Info size={15} />
          <div>
            <div className="fundingv2-banner__title">PAD is set up. Your first automatic debit will run this Thursday.</div>
            <div>If you need to add funds before your first debit, use one of the manual options below.</div>
          </div>
        </div>
      )}
      <div className="fundingv2-statusrow">
        <span className="fundingv2-statuspill"><CheckCircle2 size={14} /> Active</span>
        <span className="fundingv2-nextpull">
          <CalendarClock size={14} /> {state === "pending_first_debit" ? "First" : "Next"} debit: Thursday, {nextThursday}
        </span>
        <span className="fundingv2-muted">Funds credited 5 business days after the debit</span>
      </div>

      <div className="fundingv2-detailgrid">
        {/* banking */}
        <div className="fundingv2-detail">
          <div className="fundingv2-detail__head">
            <span className="fundingv2-detail__label">Banking</span>
            {!editBanking && !readOnly && (
              <button type="button" className="fundingv2-editlink" onClick={openEditBanking}><Pencil size={13} /> Edit</button>
            )}
          </div>
          {!editBanking ? (
            <>
              <div className="fundingv2-detail__row"><span>Transit</span><strong>{pad?.transitNo}</strong></div>
              <div className="fundingv2-detail__row"><span>Institution</span><strong>{pad?.bankNo}</strong></div>
              <div className="fundingv2-detail__row"><span>Account</span><strong>{pad?.bankAccountMasked}</strong></div>
            </>
          ) : (
            <>
              <div className="fundingv2-hint" style={{ marginBottom: 8 }}>
                Changes can take 1 to 2 business days to take effect. A debit scheduled before then uses your current banking information.
              </div>
              <div className="fundingv2-editfields">{bankingFields("pad-edit", true)}</div>
              <label className="fundingv2-consent" style={{ fontSize: 13 }}>
                <input type="checkbox" checked={bankConsent} onChange={(e) => setBankConsent(e.target.checked)} />
                <span>I confirm these bank details are correct and I am authorized to debit this account.</span>
              </label>
              <div className="fundingv2-actions">
                <button className="fundingv2-btn fundingv2-btn--ghost fundingv2-btn--sm" onClick={() => setEditBanking(false)}>Cancel</button>
                <button className="fundingv2-btn fundingv2-btn--sm" disabled={busy} onClick={saveBanking}><Check size={15} /> Save</button>
              </div>
            </>
          )}
        </div>

        {/* float */}
        <div className="fundingv2-detail">
          <div className="fundingv2-detail__head">
            <span className="fundingv2-detail__label">Weekly Float</span>
            {!editFloat && !readOnly && (
              <button type="button" className="fundingv2-editlink" onClick={openEditFloat}><Pencil size={13} /> Edit</button>
            )}
          </div>
          {!editFloat ? (
            <div className="fundingv2-detail__val">{fmt(pad?.floatAmount || 0)}</div>
          ) : (
            <>
              <div className="fundingv2-hint" style={{ marginBottom: 8 }}>
                Changes can take 1 to 2 business days to take effect. A debit scheduled before then uses your current float amount.
              </div>
              <div className="fundingv2-input-money">
                <span>$</span>
                <input
                  className="fundingv2-input"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-label="Weekly float amount in dollars"
                  value={floatAmount}
                  onChange={(e) => { const v = e.target.value; if (v === "" || MONEY_RE.test(v)) setFloatAmount(v); }}
                />
              </div>
              {floatAmount.trim() !== "" && floatNum === 0 && (
                <div className="fundingv2-hint">
                  With a $0 float we only pull funds to cover approved claims that are already waiting. Your account won't carry a standing balance.
                </div>
              )}
              <div className="fundingv2-actions">
                <button className="fundingv2-btn fundingv2-btn--ghost fundingv2-btn--sm" onClick={() => setEditFloat(false)}>Cancel</button>
                <button className="fundingv2-btn fundingv2-btn--sm" disabled={busy} onClick={saveFloat}><Check size={15} /> Save</button>
              </div>
            </>
          )}
          <div className="fundingv2-hint">Suggested: {fmt(pad?.suggestedFloat || 0)}, your plan's combined annual limits ÷ 12.</div>
        </div>
      </div>

      <div className="fundingv2-padfoot">
        {pad?.authorizedByName && (
          <span>Authorized by {pad.authorizedByName}{pad.authorizedAt ? " · " + fmtDate(pad.authorizedAt) : ""}</span>
        )}
        {pad?.updatedByName && pad?.updatedAt && (
          <span>Last updated by {pad.updatedByName} · {fmtDate(pad.updatedAt)}</span>
        )}
      </div>

      {(agreementReady || !readOnly) && (
        <div className="fundingv2-actionrow">
          {agreementReady && (
            <button type="button" className="fundingv2-actionlink" onClick={downloadAgreement}>
              <FileDown size={13} /> Download PAD Agreement (PDF)
            </button>
          )}
          {!readOnly && (
            <button type="button" className="fundingv2-actionlink fundingv2-actionlink--warn" onClick={() => setShowCancel(!showCancel)}>
              <XCircle size={13} /> Request PAD cancellation
            </button>
          )}
        </div>
      )}
      {showCancel && (
        <div className="fundingv2-cancelbox">
          <div className="fundingv2-cancelbox__title">Cancel Pre-Authorized Debit?</div>
          <div className="fundingv2-cancelbox__body">
            This pauses your automatic debits. Any debit already generated for this week will still process. Your account reverts to manual funding, and claims may be delayed if your balance runs low. Contact support to reactivate.
          </div>
          <div className="fundingv2-actions" style={{ marginTop: 0 }}>
            <button className="fundingv2-btn fundingv2-btn--danger fundingv2-btn--sm" disabled={busy} onClick={submitCancel}>Pause my PAD debits</button>
            <button className="fundingv2-btn fundingv2-btn--ghost fundingv2-btn--sm" onClick={() => setShowCancel(false)}>Never mind</button>
          </div>
        </div>
      )}
      {howFooter()}
    </div>
  );

  const setupBody = () => (
    <div className="fundingv2-pad" onClick={(e) => e.stopPropagation()}>
      {!showWizard ? (
        <>
          {howPanel(true)}
          {!readOnly ? (
            <div className="fundingv2-actions">
              <button className="fundingv2-btn" onClick={() => { resetDrafts(); setShowWizard(true); }}>
                Set Up Pre-Authorized Debit <ArrowRight size={15} />
              </button>
            </div>
          ) : (
            <div className="fundingv2-hint" style={{ marginTop: 12 }}>
              Your plan admin access is read-only. A full-access administrator can set up PAD here.
            </div>
          )}
        </>
      ) : (
        <>
          <div className="fundingv2-steps" aria-label="PAD setup steps">
            <div
              className={"fundingv2-step" + (padStep > 1 ? " fundingv2-step--done" : " fundingv2-step--active")}
              aria-current={padStep === 1 ? "step" : undefined}
              onClick={() => padStep > 1 && setPadStep(1)}
            >
              <span className="fundingv2-step__num">{padStep > 1 ? <Check size={14} /> : "1"}</span> Agreement
            </div>
            <div className="fundingv2-step-line" aria-hidden="true" />
            <div
              className={"fundingv2-step" + (padStep > 2 ? " fundingv2-step--done" : padStep === 2 ? " fundingv2-step--active" : "")}
              aria-current={padStep === 2 ? "step" : undefined}
              onClick={() => padStep > 2 && setPadStep(2)}
            >
              <span className="fundingv2-step__num">{padStep > 2 ? <Check size={14} /> : "2"}</span> Banking &amp; Weekly Float
            </div>
            <div className="fundingv2-step-line" aria-hidden="true" />
            <div className={"fundingv2-step" + (padStep === 3 ? " fundingv2-step--active" : "")} aria-current={padStep === 3 ? "step" : undefined}>
              <span className="fundingv2-step__num">3</span> Sign &amp; Authorize
            </div>
          </div>

          {padStep === 1 && (
            <>
              <div className="fundingv2-subhead"><FileText size={14} color="#559e83" /> Pre-Authorized Debit Agreement</div>
              <div className="fundingv2-agreement" tabIndex={0} role="region" aria-label="Pre-Authorized Debit agreement">
                {PAD_AGREEMENT.map((p, i) => <p key={i}>{p}</p>)}
              </div>
              <div className="fundingv2-actions">
                <button className="fundingv2-btn" onClick={() => setPadStep(2)}>I've read the agreement. Continue <ArrowRight size={15} /></button>
                <button className="fundingv2-btn fundingv2-btn--ghost" onClick={() => setShowWizard(false)}>Cancel</button>
              </div>
            </>
          )}

          {padStep === 2 && (
            <>
              <div className="fundingv2-subhead"><Landmark size={14} color="#559e83" /> Business Bank Account</div>
              <div className="fundingv2-fields">{bankingFields("pad", false)}</div>

              <div className="fundingv2-subhead"><Repeat size={14} color="#559e83" /> Weekly Float</div>
              <p className="fundingv2-muted">
                Your weekly float is the amount of money you keep in your National HealthClaim account at all times. Think of it as a buffer: when claims are approved, they're paid from whatever is in your account at that point. The higher your float, the more likely there's money available to cover claims right away, without waiting for the next Thursday debit.
              </p>
              <p className="fundingv2-muted">
                Each Thursday, if your balance has dipped below your float amount, we automatically top it back up.
              </p>
              <div className="fundingv2-floatbox__label">How your float choice affects claim reimbursements:</div>
              <div className="fundingv2-floatbox">
                <div className="fundingv2-floatbox__row">
                  <strong className="is-funded">With a funded float:</strong> Money is available in your account when claims are approved. Reimbursements can process without waiting for the next funding cycle.
                </div>
                {/* $0 stays supported but unadvertised; explain it only once they type 0 */}
                {floatAmount.trim() !== "" && floatNum === 0 && (
                  <div className="fundingv2-floatbox__row">
                    <strong>With a $0 float:</strong> We only pull funds to cover approved claims that are already waiting. Your account won't carry a standing balance. Funding happens each Thursday based on what's pending.
                  </div>
                )}
              </div>
              <div className="fundingv2-banner fundingv2-banner--info" style={{ maxWidth: 620 }}>
                <CalendarClock size={15} />
                <div><strong>Funds take 5 business days to clear</strong> after each Thursday debit.</div>
              </div>
              <div className="fundingv2-field fundingv2-field--float">
                <div className="fundingv2-input-money">
                  <span>$</span>
                  <input
                    className={"fundingv2-input" + (bankTouched && bankErr.float ? " fundingv2-input--error" : "")}
                    inputMode="decimal"
                    autoComplete="off"
                    aria-label="Weekly float amount in dollars"
                    value={floatAmount}
                    onChange={(e) => { const v = e.target.value; if (v === "" || MONEY_RE.test(v)) { setFloatAmount(v); setConfirmFloat(false); } }}
                  />
                </div>
                {fieldHint(
                  bankTouched && bankErr.float,
                  "Suggested: " + fmt(pad?.suggestedFloat || 0) + ", your employees' combined annual limits (" + fmt(pad?.annualLimits || 0) + ") ÷ 12. Adjust as needed.",
                  "Enter a weekly float amount."
                )}
              </div>

              {confirmFloat ? (
                <div className="fundingv2-cancelbox fundingv2-cancelbox--confirm">
                  <div className="fundingv2-cancelbox__title">Confirm your weekly float: {fmt(floatNum)}</div>
                  <div className="fundingv2-cancelbox__body">
                    {floatNum === 0
                      ? "With a $0 float, we only debit to cover approved claims that are already waiting. You can change this any time."
                      : "Each Thursday, if your balance has dipped below " + fmt(floatNum) + ", we top it back up. You can change this any time."}
                  </div>
                  <div className="fundingv2-actions" style={{ marginTop: 0 }}>
                    <button className="fundingv2-btn fundingv2-btn--sm" onClick={confirmFloatContinue}>Confirm and continue <ArrowRight size={15} /></button>
                    <button className="fundingv2-btn fundingv2-btn--ghost fundingv2-btn--sm" onClick={() => setConfirmFloat(false)}>Change amount</button>
                  </div>
                </div>
              ) : (
                <div className="fundingv2-actions">
                  <button className="fundingv2-btn fundingv2-btn--ghost" onClick={() => setPadStep(1)}><ArrowLeft size={15} /> Back</button>
                  <button className="fundingv2-btn" onClick={continueBanking}>Continue <ArrowRight size={15} /></button>
                </div>
              )}
            </>
          )}

          {padStep === 3 && (
            <>
              <div className="fundingv2-subhead"><Lock size={14} color="#559e83" /> Sign &amp; Authorize</div>
              <p className="fundingv2-muted">By entering your name below, you authorize National HealthClaim to debit the bank account you provided, on the schedule described in the agreement.</p>

              <div className="fundingv2-detail fundingv2-detail--review">
                <div className="fundingv2-detail__head">
                  <span className="fundingv2-detail__label">You are authorizing</span>
                  <button type="button" className="fundingv2-editlink" onClick={() => setPadStep(2)}><Pencil size={13} /> Edit</button>
                </div>
                <div className="fundingv2-detail__row"><span>Bank account</span><strong>{transit}-{institution} · {maskDraft(account)}</strong></div>
                <div className="fundingv2-detail__row"><span>Weekly float</span><strong>{fmt(floatNum)}</strong></div>
                <div className="fundingv2-detail__row"><span>Debit schedule</span><strong>Thursdays, as needed</strong></div>
                <div className="fundingv2-detail__row"><span>Funds credited</span><strong>5 business days after debit</strong></div>
              </div>
              <details className="fundingv2-agreement fundingv2-agreement--toggle">
                <summary>Review the Pre-Authorized Debit Agreement</summary>
                {PAD_AGREEMENT.map((p, i) => <p key={i}>{p}</p>)}
              </details>

              <div className="fundingv2-field" style={{ maxWidth: 380 }}>
                <label htmlFor="pad-signer">Signing Officer Full Name</label>
                <input
                  id="pad-signer"
                  className="fundingv2-input"
                  autoComplete="off"
                  value={signingName}
                  placeholder="e.g. Jane Smith"
                  maxLength={100}
                  onChange={(e) => setSigningName(e.target.value)}
                />
                <div className="fundingv2-hint">Must be an authorized signing officer on the bank account entered in step 2.</div>
              </div>
              <label className="fundingv2-consent">
                <input type="checkbox" checked={authorized} onChange={(e) => setAuthorized(e.target.checked)} />
                <span>I am authorized to enter into this Pre-Authorized Debit agreement on behalf of the company, I confirm the bank account details are correct, and I agree to the Pre-Authorized Debit Agreement above.</span>
              </label>
              <div className="fundingv2-actions">
                <button className="fundingv2-btn fundingv2-btn--ghost" onClick={() => setPadStep(2)}><ArrowLeft size={15} /> Back</button>
                <button className="fundingv2-btn" disabled={busy} onClick={submitPad}><ShieldCheck size={16} /> Submit Authorization</button>
              </div>
            </>
          )}
          {howFooter()}
        </>
      )}
    </div>
  );

  if (loading) {
    return <CustomLoading />;
  }

  const balance = summary?.fundingBalance ?? 0;
  const shortfall = summary?.shortfall ?? 0;
  const claimCount = summary?.approvedClaimCount ?? 0;
  const claimTotal = summary?.approvedClaimTotal ?? 0;

  // pa-facing badge is binary: pad is either debiting or it isn't. the card body explains why,
  // and balance health lives in the funding summary below. ops keeps the six detailed states.
  const headBadge = () => {
    // setup state: no badge; manual funding methods are perfectly valid without pad
    if (!padConfigured) {
      return null;
    }
    return padRunning
      ? <div className="fundingv2-badge fundingv2-badge--ok"><ShieldCheck size={15} /> PAD Active</div>
      : <div className="fundingv2-badge fundingv2-badge--low"><AlertTriangle size={15} /> PAD Inactive</div>;
  };

  const padHeaderMeta = () => {
    if (padRunning) {
      return <span>Automatic top-up every Thursday as needed · Bank: {pad?.transitNo}-{pad?.bankNo} · {pad?.bankAccountMasked} · Weekly float: {fmt(pad?.floatAmount || 0)}</span>;
    }
    if (padConfigured) {
      return <span>Automatic debits are paused</span>;
    }
    return (
      <>
        <span>Same-day claim payments from your balance</span>
        <span>Weekly debit each Thursday · credited 5 business days later</span>
      </>
    );
  };

  // once enrolled, pad is the account's funding state rather than a method choice,
  // so it renders above the summary; before enrollment it stays in the method chooser
  const padCard = (
    <div
      className={"fundingv2-method fundingv2-method--pad" + (padConfigured || method === "pad" ? " fundingv2-method--selected" : "")}
      role={!padConfigured ? "radio" : undefined}
      aria-checked={!padConfigured ? method === "pad" : undefined}
      tabIndex={!padConfigured ? 0 : undefined}
      onClick={() => !padConfigured && setMethod("pad")}
      onKeyDown={(e) => {
        if (padConfigured || e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setMethod("pad"); }
      }}
    >
      <div className="fundingv2-ribbon">
        {padRunning ? <CheckCircle2 size={11} /> : <Sparkles size={11} />}
        {padRunning ? "Active" : padConfigured ? "Inactive" : "Recommended"}
      </div>
      {padConfigured
        ? <CheckCircle2 size={18} color={padRunning ? "#2f7a5b" : "#8593a3"} style={{ marginTop: 3, flexShrink: 0 }} />
        : <div className={"fundingv2-radio" + (method === "pad" ? " fundingv2-radio--on" : "")} aria-hidden="true" />}
      <div className="fundingv2-method__body">
        <div className="fundingv2-method__title">
          <span className="fundingv2-method__icon fundingv2-method__icon--pad"><Landmark size={16} /></span>
          <span>Pre-Authorized Debit</span>
        </div>
        <div className="fundingv2-method__meta">{padHeaderMeta()}</div>

        {justEnrolled
          ? confirmationBody()
          : padRunning
          ? activeBody()
          : padConfigured
          ? suspendedBody()
          : method === "pad" && setupBody()}
      </div>
    </div>
  );

  return (
    <div className="fundingv2" data-policy-id={policyId}>
      <div className="fundingv2-head">
        <div>
          <h2 className="fundingv2-head__title"><Wallet size={22} color="#559e83" /> Funding Management</h2>
          <div className="fundingv2-head__sub">
            {padRunning
              ? "Pre-Authorized Debit is active. Review your PAD details and balance below."
              : padConfigured
              ? "Pre-Authorized Debit is paused. Review your PAD details and funding options below."
              : "Review your current account balance, then choose how you fund employee claims."}
          </div>
        </div>
        {headBadge()}
      </div>

      {/* pad details above the summary once enrolled */}
      {padConfigured && <div className="fundingv2-methods" style={{ marginBottom: 16 }}>{padCard}</div>}

      {/* account funding summary */}
      <div className="fundingv2-card fundingv2-panel">
        <div className="fundingv2-panel__head">
          <h3 className="fundingv2-section-title"><Receipt size={18} color="#6366f1" /> Account Funding Summary</h3>
          <div className="fundingv2-muted">As of {today}</div>
        </div>
        <div className="fundingv2-summary">
          <div className="fundingv2-summary__row">
            <span>Current Account Funds</span>
            <span className={"fundingv2-summary__val" + (balance > 0 ? " fundingv2-summary__val--ok" : "")}>{fmt(balance)}</span>
          </div>
          {(summary?.onHoldAmount ?? 0) > 0 && (
            <div className="fundingv2-summary__row">
              <span>On Hold Amount</span>
              <span className="fundingv2-summary__val">{fmt(summary!.onHoldAmount)}</span>
            </div>
          )}
          <div className="fundingv2-summary__row">
            <span>{claimCount} Approved {claimCount === 1 ? "Claim" : "Claims"} Waiting Reimbursement</span>
            <span className="fundingv2-summary__val">{fmt(claimTotal)}</span>
          </div>
          <div className="fundingv2-summary__row fundingv2-summary__row--shortfall">
            <span>Minimum Amount Due (Shortfall)</span>
            <span className={"fundingv2-summary__val" + (shortfall > 0 ? " fundingv2-summary__val--warn" : "")}>{fmt(shortfall)}</span>
          </div>
        </div>
      </div>

      {/* funding methods: manual top-ups once enrolled, full chooser otherwise */}
      <h3 className="fundingv2-section-title"><Banknote size={18} color="#559e83" /> {padConfigured ? "Add Funds" : "Funding Method"}</h3>
      <div className="fundingv2-muted" style={{ margin: "-6px 2px 12px" }}>
        {padRunning
          ? "One-off top-ups that don't change your Pre-Authorized Debit."
          : padConfigured
          ? "Use any of these methods to keep your account funded while PAD is paused."
          : "Choose how you'd like to keep your account funded."}
      </div>
      <div className="fundingv2-methods" role={!padConfigured ? "radiogroup" : undefined} aria-label={!padConfigured ? "Funding method" : undefined}>
        {!padConfigured && (
          <>
            <div className="fundingv2-methods__divider">
              <span className="fundingv2-methods__divider-title">Automatic</span>
              <span className="fundingv2-muted">Set up once. Weekly debits keep your balance funded as needed.</span>
            </div>
            {padCard}
            <div className="fundingv2-methods__divider">
              <span className="fundingv2-methods__divider-title">Manual</span>
              <span className="fundingv2-muted">Add funds yourself whenever needed.</span>
            </div>
          </>
        )}
        {OTHER_METHODS.map((m) => {
          const open = method === m.key;
          return (
            <div
              key={m.key}
              className={"fundingv2-method" + (open ? (padConfigured ? " fundingv2-method--open" : " fundingv2-method--selected") : "")}
              role={padConfigured ? "button" : "radio"}
              aria-checked={padConfigured ? undefined : open}
              aria-expanded={padConfigured ? open : undefined}
              tabIndex={0}
              onClick={() => setMethod(padConfigured && open ? "pad" : m.key)}
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return;
                if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setMethod(padConfigured && open ? "pad" : m.key); }
              }}
            >
              {padConfigured
                ? <ChevronDown size={18} className={"fundingv2-method__chev" + (open ? " fundingv2-method__chev--open" : "")} style={{ marginTop: 3, flexShrink: 0 }} />
                : <div className={"fundingv2-radio" + (open ? " fundingv2-radio--on" : "")} aria-hidden="true" />}
              <div className="fundingv2-method__body">
                <div className="fundingv2-method__title"><span className={"fundingv2-method__icon fundingv2-method__icon--" + m.key}><m.Icon size={16} /></span> {m.title}</div>
                <div className="fundingv2-method__meta">
                  <span>{m.timeline}</span>
                </div>
                {open && (
                  <div className="fundingv2-pad" onClick={(e) => e.stopPropagation()}>
                    {renderMethodBody(m.key)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="fundingv2-note" style={{ textAlign: "center" }}>
        All existing methods of funding are still accepted. Please reach out to us at{" "}
        <a href="mailto:support@nationalhealthclaim.com">support@nationalhealthclaim.com</a> with any questions.
      </div>
      <div className="fundingv2-note">{FUNDING_NOTE}</div>

      {/* communication settings: hidden while pad is running; the funding notification
          webjob skips those policies, so there is nothing for the pa to control */}
      {!padRunning && (
      <div className="fundingv2-card fundingv2-comm">
        <div className="fundingv2-comm__head">
          <h3 className="fundingv2-section-title"><BellRing size={18} color="#d97706" /> Funding Communication Settings</h3>
          <div className="fundingv2-muted">Control how and when we notify you about your account balance and funding reminders.</div>
        </div>
        <div className="fundingv2-comm__row">
          <div>
            <div className="fundingv2-comm__label">Funding Email Frequency</div>
            <div className="fundingv2-comm__desc">We email you when approved claims are waiting on funding. Choose how often you'd like those reminders.</div>
          </div>
          <div className="fundingv2-comm__control">
            <select
              className="fundingv2-input"
              aria-label="Funding email frequency"
              value={emailFreq}
              disabled={readOnly}
              onChange={(e) => changeEmailFreq(Number(e.target.value))}
            >
              {FUNDING_EMAIL_FREQ.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
        <div className="fundingv2-comm__row">
          <div>
            <div className="fundingv2-comm__label">Minimum Balance Alert</div>
            <div className="fundingv2-comm__desc">Set a minimum balance and we'll email you when your account dips below it. Useful for staying ahead of reimbursements without checking manually.</div>
          </div>
          <div className="fundingv2-comm__control">
            {!editMinBal ? (
              <div className="fundingv2-threshold__box" style={{ position: "relative" }}>
                <div className="fundingv2-muted">Minimum Balance</div>
                <div className="fundingv2-threshold__val">{minimumBalance ? fmt(minimumBalance) : "Not set"}</div>
                {!readOnly && (
                  <button type="button" className="fundingv2-edit" onClick={() => { setMinBalDraft(minimumBalance ? String(minimumBalance) : ""); setEditMinBal(true); }}>Edit</button>
                )}
              </div>
            ) : (
              <div>
                <div className="fundingv2-input-money">
                  <span>$</span>
                  <input
                    className="fundingv2-input"
                    inputMode="decimal"
                    autoComplete="off"
                    aria-label="Minimum balance in dollars"
                    placeholder="0.00"
                    value={minBalDraft}
                    onChange={(e) => { const v = e.target.value; if (v === "" || MONEY_RE.test(v)) setMinBalDraft(v); }}
                  />
                </div>
                <div className="fundingv2-actions" style={{ marginTop: 10 }}>
                  <button className="fundingv2-btn fundingv2-btn--ghost fundingv2-btn--sm" onClick={() => setEditMinBal(false)}>Cancel</button>
                  <button className="fundingv2-btn fundingv2-btn--sm" disabled={busy} onClick={saveMinimumBalance}><Check size={15} /> Save</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* shortfall funding request */}
      <div className="fundingv2-card fundingv2-shortfall">
        <div>
          <h3 className="fundingv2-section-title"><FileDown size={18} color="#1e85e5" /> Shortfall Funding Request</h3>
          <div className="fundingv2-muted">
            {shortfall > 0
              ? "Current shortfall: " + fmt(shortfall) + ". Generate a printable request form for your accounting department."
              : "No shortfall right now. You can still generate a printable request form for your accounting department any time."}
          </div>
        </div>
        <div className="fundingv2-shortfall__form">
          <label className="fundingv2-muted" htmlFor="shortfall-amount">Funding amount</label>
          <div className="fundingv2-input-money">
            <span>$</span>
            <input
              id="shortfall-amount"
              className="fundingv2-input"
              inputMode="decimal"
              autoComplete="off"
              placeholder="0.00"
              value={shortfallAmount}
              onChange={(e) => { const v = e.target.value; if (v === "" || MONEY_RE.test(v)) setShortfallAmount(v); }}
            />
          </div>
          <button className="fundingv2-btn" style={{ width: "100%" }} onClick={generateShortfallForm}>
            <FileDown size={16} /> Generate Form
          </button>
        </div>
      </div>
    </div>
  );
}
